import { FaireMarketIcon } from "@faire/deprecated-retailer-visitor-shared/features/FaireMarkets/FaireMarketIcon";
import { useFaireDirectBrand } from "@faire/deprecated-retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useMarketStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { useQuery as usePromotionalEventBrandParticipatingStatusQuery } from "@faire/web-api--source/endpoints/www/api/v2/brands/promotional-event/eventType/brands/brandToken/participating-status/get-hooks";
import { url as faireMarketsUrl } from "@faire/web-api--source/routes/www/markets";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { FAIRE_DIRECT_BRAND_QUERY_PARAM } from "@faire/web/common/routes/faire-direct/FAIRE_DIRECT_BRAND_QUERY_PARAM";
import { Button as ButtonDeprecated } from "@faire/web/slate-deprecated/Button";
import { Button } from "@faire/slate/components/button";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { FaireMarketTheme } from "@faire/web/slate/Theme/EventsTheme";
import { getEventTheme } from "@faire/web/slate/Theme/eventsThemeColors";
import { EventsThemeLogo } from "@faire/web/slate/Theme/EventsThemeLogo";
import { EventsThemeShapeCombination } from "@faire/web/slate/Theme/EventsThemeShapeCombination";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { differenceInDays } from "date-fns/differenceInDays";
import React from "react";
import styled from "styled-components";

const DEFAULT_TEXT_COLOR = getEventTheme("faireMarket").promoTextInverse;

export const MarketEventDirectSection: React.FC<{ brandToken: string }> = ({
  brandToken,
}) => {
  const {
    currentEventType,
    marketName,
    mainEventEndAt,
    earlyAccessStartAt,
    latestMarketMainEventRangeDate: mainEventRangeDate,
  } = useMarketStore();

  const { isMobile, isTabletAndAbove } = useViewport();
  const { data } = usePromotionalEventBrandParticipatingStatusQuery(
    currentEventType,
    brandToken
  );

  const learnMoreLink = useGenerateLearnMoreFaireDirectLink();
  const learnMoreText = (
    <StrictLocalMsg
      id="NK2oLm"
      defaultMessage="Learn More"
      description={{
        text: "Once clicked, the user is redirected to another page to learn more about the Winter Market.",
      }}
    />
  );

  const shouldShowBanner = !!data?.brand_participating_status;
  if (!shouldShowBanner) {
    return null;
  }

  return (
    <ThemeProvider theme={FaireMarketTheme}>
      <FullWidthWrapper>
        <StyledMarketShapeCombo
          frontShapeColor={getEventTheme("faireMarket").decorative6 ?? ""}
          backShapeColor={getEventTheme("faireMarket").decorative5 ?? ""}
          width="308px"
          height="194px"
        />
        {isMobile ? <MobileMarketIcon /> : null}
        <Wrapper>
          <Row>
            {isMobile ? null : (
              <>
                <StyledMarketPrimaryLogo isInverse />
                <Spacer width="9x" />
              </>
            )}
            <Content>
              <Typography
                variant={{
                  mobileAndAbove: "paragraphSansMedium",
                  tabletAndAbove: "subheadingSansMedium",
                }}
                color={DEFAULT_TEXT_COLOR}
              >
                <StrictLocalMsg
                  id="0c6jtm"
                  defaultMessage="{mainEventRangeDate}, plus Insider early access"
                  description={{
                    text: "Faire Market dates including our Insider program",
                  }}
                  values={{ mainEventRangeDate }}
                />
              </Typography>
              <Spacer height="1x" />
              <Typography
                variant="displaySSerifRegular"
                color={DEFAULT_TEXT_COLOR}
              >
                <StrictLocalMsg
                  id="xkNY+T"
                  defaultMessage="We'll be at {marketEventName}!"
                  description={{
                    text: "Indicates that the brand will be at the Faire Market event.",
                  }}
                  values={{
                    marketEventName: marketName,
                  }}
                />
              </Typography>
              <Spacer height="1x" />
              <Typography
                variant="paragraphSansRegular"
                color={DEFAULT_TEXT_COLOR}
              >
                <StrictLocalMsg
                  id="7aBGRV"
                  defaultMessage="Shop the best deals on our brand at Faire’s 4-day virtual trade show. The event is free for all Faire retailers!"
                  description={{
                    text: "Indicates that the Faire Market event will be available for a number of days and is free for retailers.",
                  }}
                  values={{
                    daysNumber:
                      differenceInDays(mainEventEndAt, earlyAccessStartAt) + 1,
                  }}
                />
              </Typography>
              {isMobile ? null : (
                <>
                  <Spacer height="1x" />
                  <Typography variant="paragraphSansRegular">
                    <ButtonDeprecated
                      variant="tertiary"
                      tertiaryColor={DEFAULT_TEXT_COLOR}
                      linkTo={learnMoreLink}
                    >
                      {learnMoreText}
                    </ButtonDeprecated>
                  </Typography>
                </>
              )}
            </Content>
          </Row>
          {isTabletAndAbove ? null : (
            <>
              <Spacer height="2x" />
              <Button variant="secondary" linkTo={learnMoreLink}>
                {learnMoreText}
              </Button>
            </>
          )}
        </Wrapper>
      </FullWidthWrapper>
    </ThemeProvider>
  );
};

const useGenerateLearnMoreFaireDirectLink = () => {
  const { faireDirectBrandAlias } = useFaireDirectBrand();
  let link = faireMarketsUrl();

  if (faireDirectBrandAlias) {
    link += `?${FAIRE_DIRECT_BRAND_QUERY_PARAM}=${faireDirectBrandAlias}`;
  }

  return link;
};

const FullWidthWrapper = styled.div`
  position: relative;
  background-color: ${getEventTheme("faireMarket").primaryDark};
  overflow: hidden;
  padding: ${getSpacing("4x", "3x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("8x", 96)};
  }
`;

const Wrapper = styled(Column)`
  position: relative;

  ${responsiveMediaQueries.desktopAndAbove} {
    margin: 0 auto;
    max-width: 1120px;
  }
`;

const Content = styled(Column).attrs({
  align: "flex-start",
})`
  max-width: 471px;
  z-index: 1;
  padding-right: ${getSpacing("6x")};
`;

const StyledMarketPrimaryLogo = styled(EventsThemeLogo.faireMarket.primary)`
  width: 136px !important;
  height: auto !important;
  margin-bottom: ${getSpacing("3x")};

  ${responsiveMediaQueries.mobile} {
    width: 96px !important;
    margin-bottom: ${getSpacing("2x")};
  }
`;

const StyledMarketShapeCombo = styled(
  EventsThemeShapeCombination.faireMarket.comboA
)`
  position: absolute;
  right: -96px;
  bottom: 0;

  &&& {
    ${responsiveMediaQueries.tabletAndBelow} {
      display: none;
    }
  }
`;

const MobileMarketIcon = styled(FaireMarketIcon).attrs({
  color: getEventTheme("faireMarket").secondary,
  size: "92px",
})`
  position: absolute;
  right: -32px;
`;
